import React, { useState } from 'react';
import { Clock, CheckCircle2, PlayCircle, XCircle, User, Scissors, Smartphone, CalendarDays, GripVertical } from 'lucide-react';

const COLUMNS = [
  { id: 'pendiente', label: 'Pendiente', color: 'text-amber-500 bg-amber-500', borderColor: 'border-amber-500/30', icon: Clock },
  { id: 'confirmado', label: 'Confirmado', color: 'text-blue-500 bg-blue-500', borderColor: 'border-blue-500/30', icon: CalendarDays },
  { id: 'en proceso', label: 'En Proceso', color: 'text-purple-500 bg-purple-500', borderColor: 'border-purple-500/30', icon: PlayCircle },
  { id: 'completado', label: 'Completado', color: 'text-emerald-500 bg-emerald-500', borderColor: 'border-emerald-500/30', icon: CheckCircle2 },
  { id: 'cancelado', label: 'Cancelado', color: 'text-rose-500 bg-rose-500', borderColor: 'border-rose-500/30', icon: XCircle }
];

export default function KanbanBoard({ appointments, onUpdateStatus }) {
  const [draggedId, setDraggedId] = useState(null);

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    // Agregamos un ligero delay visual para la tarjeta original
    setTimeout(() => {
      const el = document.getElementById(`kanban-card-${id}`);
      if (el) el.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e, id) => {
    setDraggedId(null);
    const el = document.getElementById(`kanban-card-${id}`);
    if (el) el.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (id && draggedId === id) {
      // Validacion 100% segura
      const safeAppointments = Array.isArray(appointments) ? appointments : [];
      const appointment = safeAppointments.find(a => String(a.id) === String(id));
      const currentStatus = appointment?.status || 'pendiente';
      
      if (currentStatus !== targetStatus) {
        onUpdateStatus(id, targetStatus);
      }
    }
    setDraggedId(null);
  };

  // Agrupamos las citas por estado
  const groupedAppointments = COLUMNS.reduce((acc, col) => {
    acc[col.id] = [];
    return acc;
  }, {});

  (appointments || []).forEach(appt => {
    const status = (appt.status || 'pendiente').toLowerCase();
    if (groupedAppointments[status]) {
      groupedAppointments[status].push(appt);
    } else {
      groupedAppointments['pendiente'].push(appt);
    }
  });

  return (
    <div className="flex gap-4 overflow-x-auto pb-8 pt-2 px-2 snap-x hide-scrollbar min-h-[600px] w-full items-stretch">
      {COLUMNS.map(col => {
        const Icon = col.icon;
        const columnItems = groupedAppointments[col.id] || [];

        return (
          <div 
            key={col.id}
            className="flex-shrink-0 w-80 bg-seryi-bg border border-seryi-bg-light rounded-3xl flex flex-col shadow-lg overflow-hidden snap-center h-[70vh]"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            {/* Cabecera de la Columna */}
            <div className={`p-4 border-b ${col.borderColor} bg-seryi-bg-dark/50 backdrop-blur-sm sticky top-0 z-10 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-opacity-10 ${col.color.split(' ')[1]}/10`}>
                  <Icon className={`${col.color.split(' ')[0]}`} size={16} />
                </div>
                <h3 className="font-bold text-sm text-seryi-heading uppercase tracking-wide">{col.label}</h3>
              </div>
              <span className="text-[10px] font-bold bg-seryi-bg-dark px-2 py-1 rounded-full text-seryi-text border border-seryi-bg-light">
                {columnItems.length}
              </span>
            </div>

            {/* Zona de Tarjetas */}
            <div className={`flex-1 p-3 flex flex-col gap-3 overflow-y-auto transition-colors ${draggedId ? 'bg-seryi-bg-dark/30' : ''}`}>
              {columnItems.length === 0 ? (
                <div className="h-24 border-2 border-dashed border-seryi-bg-light rounded-2xl flex items-center justify-center text-seryi-text text-xs italic opacity-50 m-2">
                  Arrastra aquí
                </div>
              ) : (
                columnItems.map(appt => (
                  <div
                    key={appt.id}
                    id={`kanban-card-${appt.id}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, appt.id)}
                    onDragEnd={(e) => handleDragEnd(e, appt.id)}
                    className="bg-seryi-bg-dark border border-seryi-bg-light p-4 rounded-2xl shadow-sm hover:shadow-md hover:border-seryi-green/50 transition-all cursor-grab active:cursor-grabbing group"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <User size={14} className="text-seryi-green shrink-0" />
                        <h4 className="font-bold text-sm text-seryi-heading truncate">{appt.client || 'Cliente'}</h4>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-slate-500">
                        <GripVertical size={14} />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2 text-xs text-seryi-text bg-seryi-bg p-2 rounded-xl border border-seryi-bg-light/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <Scissors size={12} className="text-indigo-400 shrink-0" />
                          <span className="truncate">{appt.service || 'Servicio General'}</span>
                        </div>
                        {appt.price > 0 && (
                          <span className="text-[10px] font-bold text-emerald-400 shrink-0 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                            ${Number(appt.price).toLocaleString('es-CO')}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-seryi-bg-light/50">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-seryi-text font-bold uppercase tracking-widest mb-0.5">Fecha / Hora</span>
                          <span className="text-xs font-bold text-seryi-heading bg-seryi-bg px-1.5 py-0.5 rounded text-center border border-seryi-bg-light/50">
                            {appt.time || 'Sin fecha'}
                          </span>
                        </div>
                        {appt.phone && (
                          <a 
                            href={`https://wa.me/${String(appt.phone).replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 p-2 rounded-xl transition-colors border border-emerald-500/20"
                            title="Contactar por WhatsApp"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Smartphone size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
